import { SiteData } from "../Data/SiteData";
import { fontPair as FontPair, fontPairs } from "../Data/FontPairData";
import { Fragment, useEffect, useRef, useState } from "react";
import SelectDropdown from "../SelectDropdown";
import { GetFontOptions } from "../StyleInputs/FontField";
import { RightCaret } from "../Icons/Carets";
import ReactDOM, { unmountComponentAtNode } from "react-dom";
import VGPopUp from "components/Controls/VGPopUp";
import CustomFontMenu from "./CustomFontMenu";
import CustomFontUpload from "./CustomFontUpload";
import { LoginUserType } from "utils/Utils";

// Storage key for font persistence
const STORAGE_KEY = 'vagaro_custom_fonts';
export default function FontPairsMenu({ siteData }: { siteData: SiteData }) {
  let isCustom = true;

  for (let i = 0; i < fontPairs.length; i++) {
    if (
      fontPairs[i].primary == siteData.siteStyling.fontPrimary &&
      fontPairs[i].secondary == siteData.siteStyling.fontSecondary
    ) {
      isCustom = false;
      break;
    }
  }

  const [isCustomFont, setIsCustomFont] = useState(isCustom);
  const containerRef = useRef<any>(null);
  const [fontOptions, setFontOptions] = useState(
    GetFontOptions(siteData, true, false)
  );
  const [uploadedFonts, setUploadedFonts] = useState<Array<any>>([]);
  const scrollRef = useRef<number>(0);
  const setSelected = (e: any) => {
    if (!containerRef.current) {
      return;
    }

    let fPairs = containerRef.current?.querySelectorAll(".font-pair");

    if (fPairs)
      for (let i = 0; i < fPairs.length; i++) {
        fPairs[i].classList.remove("selected");
      }

    if (e.target) e.target.classList.add("selected");
  };

  useEffect(() => {
    let dom = document.querySelector(".right-panel");
    if (dom) dom.scrollTop = scrollRef.current;
  });

  // Update font options whenever custom fonts change
  useEffect(() => {
    setFontOptions(GetFontOptions(siteData, true, false));
  }, [siteData.siteStyling.customFonts]);

  // Use a simple counter to force re-renders
  const [refreshCounter, setRefreshCounter] = useState(0);
  const forceRefresh = () => setRefreshCounter(prev => prev + 1);

  // Listen for custom font change events to immediately update the UI
  useEffect(() => {
    const handleFontsChanged = () => {
      // Force an immediate update of font options
      setFontOptions(GetFontOptions(siteData, true, false));

      // Force a refresh of the component
      forceRefresh();

      // Also ensure the font selections are still valid
      // Check if primary font still exists in the customFonts array
      if (siteData.siteStyling.fontPrimary && 
          siteData.siteStyling.fontPrimary !== "" && 
          !siteData.siteStyling.customFonts.some(f => {
            try {
              const fontData = JSON.parse(f);
              return fontData.family === siteData.siteStyling.fontPrimary;
            } catch {
              return f.includes(`family=${siteData.siteStyling.fontPrimary}`);
            }
          })) {
        siteData.siteStyling.fontPrimary = "";
        siteData.onUpdateStyle();
      }

      // Check if secondary font still exists in the customFonts array
      if (siteData.siteStyling.fontSecondary && 
          siteData.siteStyling.fontSecondary !== "" && 
          !siteData.siteStyling.customFonts.some(f => {
            try {
              const fontData = JSON.parse(f);
              return fontData.family === siteData.siteStyling.fontSecondary;
            } catch {
              return f.includes(`family=${siteData.siteStyling.fontSecondary}`);
            }
          })) {
        // If secondary font is no longer available, reset it
        siteData.siteStyling.fontSecondary = "";
        siteData.onUpdateStyle();
      }
    };

    // Add event listener for font changes
    document.addEventListener("vagaro-fonts-changed", handleFontsChanged);

    // Clean up event listener on component unmount
    return () => {
      document.removeEventListener("vagaro-fonts-changed", handleFontsChanged);
    };
  }, [siteData]);

  useEffect(() => {
    // Check if we need to restore fonts from localStorage
    if (!siteData.siteStyling.customFonts || siteData.siteStyling.customFonts.length === 0) {
      const savedFonts = localStorage.getItem("vagaro_custom_fonts");
      if (savedFonts) {
        try {
          const fontsArray = JSON.parse(savedFonts);
          if (Array.isArray(fontsArray) && fontsArray.length > 0) {
            // Restore fonts from localStorage to siteData
            siteData.siteStyling.customFonts = fontsArray;
            siteData.isStyleSaveDirty = true;
            siteData.isStylePublishDirty = true;
          }
        } catch (e) {
          console.warn("Could not recover fonts from localStorage", e);
        }
      }
    }

    try {
      // Process the fonts from siteData
      const loadedFonts = siteData.siteStyling.customFonts
        .filter(fontStr => {
          // Filter out non-JSON entries (like "family=" format strings)
          return fontStr && typeof fontStr === "string" && !fontStr.startsWith("family=") && fontStr.trim().startsWith("{");
        })
        .map(fontStr => {
          try {
            // Parse the JSON string back to an object
            const fontData = JSON.parse(fontStr);
            if (fontData && fontData.family && typeof fontData.family === "string") {
              // Inject the font styles into the document
              if (fontData.cssDeclaration) {
                const fontId = `font-${fontData.family.replace(/\s+/g, "-")}`;
                if (!document.getElementById(fontId)) {
                  const style = document.createElement("style");
                  style.id = fontId;
                  style.textContent = fontData.cssDeclaration;
                  document.head.appendChild(style);
                }
              } else {
                console.warn(`⚠️ Font data missing cssDeclaration for: ${fontData.family}`);
              }

              return fontData;
            }
            return null;
          } catch (e) {
            console.warn("Failed to parse font data:", e, "Original string:", fontStr);
            return null;
          }
        })
        .filter(Boolean); // Remove any null values

      setUploadedFonts(loadedFonts);
    } catch (error) {
      console.error("Error processing fonts:", error);
      setUploadedFonts([]);
    }
  }, [siteData.siteStyling.customFonts]);

  const cacheScroll = () => {
    let dom = document.querySelector(".right-panel");
    if (dom) scrollRef.current = dom.scrollTop;
  };

  const imgURLRoot = `${process.env.PUBLIC_URL}/fontpreviews/`;

  const RenderPair = ({
    pair,
    setSelected,
  }: {
    pair: FontPair;
    setSelected: Function;
  }) => {
    const selectedClass =
      pair.primary == siteData.siteStyling.fontPrimary &&
      pair.secondary == siteData.siteStyling.fontSecondary
        ? "selected"
        : "";

    const imgFileA = pair.primary.replaceAll(" ", "-").toLowerCase() + ".png";
    const imgFileB = pair.secondary.replaceAll(" ", "-").toLowerCase() + ".png";
    const imgURLA = `${imgURLRoot}${imgFileA}`;
    const imgURLB = `${imgURLRoot}${imgFileB}`;

    return (
      <button
        className={"flex-column font-pair invisible-button " + selectedClass}
        key={pair.primary + pair.secondary}
        onClick={(e) => {
          siteData.siteStyling.fontPrimary = pair.primary;
          siteData.siteStyling.fontSecondary = pair.secondary;

          cacheScroll();
          setIsCustomFont(false);
          setSelected(e);
          setTimeout(() => {
            siteData.designer && siteData.designer.updateMasterLayout();
            siteData.onUpdateStyle();
            (e.target as any).classList.add("selected");
          }, 0);
        }}
      >
        <div className="relative">
          <img alt="font pair" src={imgURLA} height={24} />
          <img
            alt="font pair"
            src={imgURLA}
            height={24}
            style={{ position: "absolute", left: "1px", bottom: 0 }}
          />
          <span style={{ display: "none" }}>{pair.primary}</span>
        </div>
        <div>
          <img    alt="font pair" src={imgURLB} height={24} />
          <span style={{ display: "none" }}>{pair.secondary}</span>
        </div>
      </button>
    );
  };

  const ManageFonts = () => {
    const PopupClose = () => {
      let dom = document.getElementById("OpenDialog");
      if (dom) unmountComponentAtNode(dom);
      siteData.onUpdateStyle();
      setFontOptions(GetFontOptions(siteData, true, false));
    };

    setTimeout(() => {
      let dom = document.getElementById("unsplash-search");
      if (dom) dom.focus();
    }, 100);

    const dom = document.getElementById("OpenDialog");

    ReactDOM.render(
      <>
        <VGPopUp
          id={"managefonts-popup"}
          show={true}
          onHide={PopupClose}
          title={
            <div className="flex-row align-center gap-med">
              Manage Google Fonts
            </div>
          }
          dialogClassName="block-edit"
          body={
            <div className="alertContent">
              <CustomFontMenu siteData={siteData} />
              <label>&#9888;{" Admin Only"}</label>
            </div>
          }
          footer={
            <Fragment>
              <button
                className="w-button w-button-primary"
                onClick={(e) => {
                  PopupClose();
                }}
              >
                Ok
              </button>
            </Fragment>
          }
          isRemoveTabModalIndex={false}
          isShowFooter={true}
          size={"modal-sm"}
          backdrop={true}
        />
      </>,
      dom
    );
  };

  const UploadCustomFonts = () => {
    const PopupClose = () => {
      console.log("PopupClose called");
      let dom = document.getElementById("OpenDialog");
      if (dom) unmountComponentAtNode(dom);
      siteData.onUpdateStyle();
      setFontOptions(GetFontOptions(siteData, true, false));
    };

    setTimeout(() => {
      let dom = document.getElementById("OpenDialog");
      if (dom) dom.focus();

      // Fix the z-index and positioning after modal renders due to Header blocking close-btn
      setTimeout(() => {
        const closeButton = document.querySelector("#uploadcustomfonts-popup .btn-close");
        if (closeButton) {
          (closeButton as HTMLElement).style.zIndex = "1070";
          (closeButton as HTMLElement).style.position = "absolute";
          (closeButton as HTMLElement).style.top = "15px";
          (closeButton as HTMLElement).style.right = "15px";
          console.log("Fixed close button position and z-index");
        }

        const builderHeader = document.querySelector(".builder-header.editor-header");
        if (builderHeader) {
          (builderHeader as HTMLElement).style.zIndex = "1050";
          console.log("Lowered builder header z-index");
        }
      }, 100);
    }, 100);

    const dom = document.getElementById("OpenDialog");

    ReactDOM.render(
      <>
        <VGPopUp
          id={"uploadcustomfonts-popup"}
          show={true}
          onHide={PopupClose}
          title={<div className="flex-row align-center gap-med"></div>}
          dialogClassName="block-edit"
          body={
            <div className="alertContent">
              <div className="alertContent">
                <CustomFontUpload siteData={siteData} />
              </div>
            </div>
          }
          footer={
            <Fragment>
              <button
                className="w-button w-button-primary"
                onClick={(e) => {
                  PopupClose();
                }}
              >
                Ok
              </button>
            </Fragment>
          }
          isRemoveTabModalIndex={false}
          isShowFooter={true}
          size={"modal-md"}
          backdrop={true}
        />
      </>,
      dom
    );
  };

  return (
    <>
      <div ref={containerRef}>
        <h2 className="panel-heading">Custom Fonts</h2>

        <button
          className="invisible-button "
          style={{
            color: "var(--input-selected-primary)",
            margin: "",
          }}
          onClick={() => {
            cacheScroll();
            UploadCustomFonts();
          }}
        >
          {"Manage Custom Fonts"}
        </button>

        <hr />

        <h2 className="panel-heading">Font Pair</h2>
        <br></br>
        <div className="flex-column" style={{ gap: "16px" }}>
          <>
            <button
              className={
                "flex-column font-pair invisible-button " +
                (isCustomFont ? " selected" : "")
              }
              style={{ padding: "24px 0px" }}
              onClick={() => {
                cacheScroll();
                if (!isCustomFont) {
                  setIsCustomFont(true);
                  siteData.siteStyling.fontPrimary = "";
                  siteData.siteStyling.fontSecondary = "";
                }
                siteData.onUpdateStyle();
              }}
            >
              <div
                className="flex-row w-full"
                style={{
                  padding: "0px 16px",
                  justifyContent: "space-between",
                  color: "#333",
                }}
              >
                <b>Custom Font Pair</b>
                <div
                  style={{
                    width: "12px",
                    transform: isCustomFont
                      ? "rotateZ(90deg)"
                      : "rotateZ(0deg)",
                    transition: ".2s",
                  }}
                >
                  <RightCaret />
                </div>
              </div>
            </button>
            <div
              className="font-paris-selection"
              style={{
                transition: ".2s",
                maxHeight: isCustomFont ? "240px" : "0px",
                background: isCustomFont ? "#e7e7e7" : "#efefef",
                borderRadius: "4px",
              }}
            >
              {isCustomFont && (
                <>
                  <div
                    className="wb-input-group no-border"
                    key={`font-dropdowns-container-${refreshCounter}`}
                    style={{
                      padding: "16px",
                      borderRadius: "4px",
                      marginTop: "-30px",
                      background: "inherit",
                    }}
                  >
                    <div className="flex-row justify-between">
                      <label style={{ width: "90px" }}>Headings</label>
                      <SelectDropdown
                        allowFilter={true}
                        options={fontOptions}
                        defaultValue={siteData.siteStyling.fontPrimary}
                        onChange={(val: string) => {
                          cacheScroll();
                          if (val != "addfont") {
                            siteData.siteStyling.fontPrimary = val;
                            siteData.onUpdateStyle();
                          } else {
                            siteData.siteStyling.fontPrimary = "";
                            setSelected(null);
                          }

                          siteData.designer &&
                            siteData.designer.updateMasterLayout();
                        }}
                      />
                    </div>
                    <div className="flex-row justify-between">
                      <label style={{ width: "90px" }}>Body</label>
                      <SelectDropdown
                        allowFilter={true}
                        options={fontOptions}
                        defaultValue={siteData.siteStyling.fontSecondary}
                        onChange={(val: string) => {
                          cacheScroll();
                          if (val != "addfont") {
                            siteData.siteStyling.fontSecondary = val;
                            siteData.onUpdateStyle();
                          } else {
                            siteData.siteStyling.fontSecondary = "";
                            setSelected(null);
                          }

                          siteData.designer &&
                            siteData.designer.updateMasterLayout();
                        }}
                      />
                    </div>
                    {siteData.userContext.userType ===
                      LoginUserType.VagaroAdmin && (
                      <>
                        <br></br>
                        <button
                          className="invisible-button "
                          style={{
                            color: "var(--input-selected-primary)",
                            marginLeft: "-5px",
                          }}
                          onClick={() => {
                            cacheScroll();
                            ManageFonts();
                          }}
                        >
                          {"Manage Google Fonts"}
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
            <hr></hr>
            {fontPairs.map((pair) => {
              return (
                <RenderPair
                  key={pair.primary + pair.secondary}
                  pair={pair}
                  setSelected={setSelected}
                />
              );
            })}
          </>
        </div>
      </div>
    </>
  );
}